import React, { useState } from 'react';

import type { TextProps } from '@mantine/core';
import { Highlight } from '@mantine/core';
import {
  Divider,
  Group,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';

import { IconSearch } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge } from '../../../shared/components/UI/Badge/Badge';
import { BuyerLogo } from '../../../shared/components/UI/BuyerLogo/BuyerLogo';
import DecisionBadge from '../../../shared/components/UI/DecisionBadge/DecisionBadge';
import { TextInput } from '../../../shared/components/UI/TextInput/TextInput';

import type { SearchResponseDTO } from '../../../shared/api/magellan/search/dto';
import { TextSeparator } from '../../../shared/components/TextSeparator';
import { formatDateInParisTZ } from '../../../shared/utils/dates';
import { useGetAccentedQueryArray } from '../hooks/useGetAccentedQueryArray.hook';
import { useSearchQuery } from '../hooks/useSearchQuery.hook';
import { useSearchResultContentHeight } from '../hooks/useSearchResultContentHeight.hook';

const MIN_QUERY_LENGTH = 2;

type SearchModalContentProps = {
  onClose: () => void;
};

export function SearchModalContent({ onClose }: SearchModalContentProps) {
  const { t } = useTranslation('search');
  const [query, setQuery] = useState('');

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  return (
    <Stack spacing="03" mah="80vh">
      <TextInput
        value={query}
        onChange={onInputChange}
        variant="filled"
        leftIcon={<IconSearch size={16} />}
        placeholder={t('input.placeholder')}
      />
      {query.length > MIN_QUERY_LENGTH ? (
        <SearchResults onClose={onClose} query={query} />
      ) : (
        <EmptySearchPlaceholder />
      )}
    </Stack>
  );
}

function EmptySearchPlaceholder() {
  const { t } = useTranslation('search');

  return (
    <Text align="center" c="gray.5" variant="sm" fw={500} lh={4.5}>
      {t('results.placeholders.emptyInput')}
    </Text>
  );
}

type SearchResultsProps = {
  onClose: () => void;
  query: string;
};

function SearchResults({ onClose, query }: SearchResultsProps) {
  const [debouncedQuery] = useDebouncedValue(query, 300);
  const { t } = useTranslation('domain');
  const { data, isLoading } = useSearchQuery(debouncedQuery);
  const totalHeight = useSearchResultContentHeight(data);
  const highlightPatterns = useGetAccentedQueryArray(query);

  if (isLoading || !data) {
    return <SearchResultsSkeleton />;
  }

  return (
    <ScrollArea
      h={totalHeight}
      styles={{ viewport: { '&>div': { display: 'block !important' } } }}
    >
      <Stack spacing="03">
        <ContentSection title={t('buyer_other')} query={query}>
          {data.buyers.map(buyer => (
            <BuyerItem
              key={buyer.id}
              buyer={buyer}
              onClose={onClose}
              highlightPatterns={highlightPatterns}
            />
          ))}
        </ContentSection>
        <Divider color="gray.1" />
        <ContentSection title={t('tender.tender_other')} query={query}>
          {data.tenders.map(tender => (
            <TenderItem
              key={tender.id}
              tender={tender}
              onClose={onClose}
              highlightPatterns={highlightPatterns}
            />
          ))}
        </ContentSection>
      </Stack>
    </ScrollArea>
  );
}

function SearchResultsSkeleton() {
  const { t } = useTranslation('domain');

  return (
    <Stack spacing="03">
      <ContentSection title={t('buyer_other')} query="">
        <Skeleton height={44} />
      </ContentSection>
      <Divider color="gray.1" />
      <ContentSection title={t('tender.tender_other')} query="">
        <Skeleton height={44} />
      </ContentSection>
    </Stack>
  );
}

type ContentSectionProps = {
  title: string;
  children: React.ReactNode;
  query: string;
};

function ContentSection({ title, children, query }: ContentSectionProps) {
  const hasChildren = React.Children.count(children) > 0;

  return (
    <Stack spacing="02">
      <Text fw={500} variant="sm" color="gray.9">
        {title}
      </Text>
      {hasChildren ? (
        <Stack spacing={0}>{children}</Stack>
      ) : (
        <EmptySection query={query} />
      )}
    </Stack>
  );
}

type TenderItemProps = {
  tender: SearchResponseDTO['tenders'][number];
  onClose: () => void;
  highlightPatterns: string[];
};

function TenderItem({ tender, onClose, highlightPatterns }: TenderItemProps) {
  const responseDeadline = tender.responseDeadline
    ? formatDateInParisTZ(tender.responseDeadline)
    : '-';

  return (
    <Link
      to={`/notices/${tender.id}`}
      style={{ textDecoration: 'none', color: 'inherit' }}
      onClick={onClose}
    >
      <Stack
        spacing="01"
        w="98%"
        p="03"
        sx={theme => ({
          '&:hover': {
            backgroundColor: theme.colors.gray[1],
            borderRadius: theme.radius.md,
          },
        })}
      >
        <HighlightedText
          highlightPatterns={highlightPatterns}
          variant="sm"
          fw={400}
          c="gray.9"
          truncate
          lineClamp={1}
        >
          {tender.title}
        </HighlightedText>
        <Group noWrap spacing="01">
          <BuyerLogo
            size="xs"
            radius="xs"
            logoURL={tender.buyerLogo}
            buyerId={tender.buyerId}
          />
          <HighlightedText
            highlightPatterns={highlightPatterns}
            truncate
            variant="sm"
            fw={400}
            c="gray.9"
            lineClamp={1}
          >
            {tender.buyerName}
          </HighlightedText>
          <TextSeparator />
          <Text truncate variant="sm" fw={400} c="gray.9" lineClamp={1}>
            {responseDeadline}
          </Text>
          {tender.decisionStatus && <TextSeparator />}
          <DecisionBadge decision={tender.decisionStatus} />
        </Group>
      </Stack>
    </Link>
  );
}

type BuyerItemProps = {
  buyer: SearchResponseDTO['buyers'][number];
  onClose: () => void;
  highlightPatterns: string[];
};

function BuyerItem({ buyer, onClose, highlightPatterns }: BuyerItemProps) {
  const { t } = useTranslation('domain');

  return (
    <Link
      to={`/buyer/${buyer.id}`}
      style={{ textDecoration: 'none', color: 'inherit' }}
      onClick={onClose}
    >
      <Group
        noWrap
        w="98%"
        spacing="01"
        p="03"
        sx={theme => ({
          '&:hover': {
            backgroundColor: theme.colors.gray[1],
            borderRadius: theme.radius.md,
          },
        })}
      >
        <BuyerLogo
          size="xs"
          radius="xs"
          logoURL={buyer.logoURL}
          buyerId={Number(buyer.id)}
        />
        <HighlightedText
          highlightPatterns={highlightPatterns}
          truncate
          variant="sm"
          fw={400}
          c="gray.9"
          maw="88%"
          lineClamp={1}
        >
          {buyer.originalName}
        </HighlightedText>
        <Badge size="xs" color="gray">
          <Text variant="xs" fw="600" color="gray.6" mr="01">
            {buyer.tenderCount} {t('tender.shorthand')}
          </Text>
        </Badge>
      </Group>
    </Link>
  );
}

type EmptySectionProps = {
  query: string;
};

function EmptySection({ query }: EmptySectionProps) {
  const { t } = useTranslation('search');

  return (
    <Stack spacing="02" align="center">
      <Text variant="sm" fw={400} color="gray.5">
        {t('results.placeholders.emptyResults')}
      </Text>
      <Text
        variant="sm"
        fw={500}
        color="gray.9"
        w="60%"
        truncate
        align="center"
      >
        {query}
      </Text>
    </Stack>
  );
}

type HighlightedTextProps = TextProps & {
  highlightPatterns: string[];
  children: string;
};

function HighlightedText({
  highlightPatterns,
  children,
  ...textProps
}: HighlightedTextProps) {
  return (
    <Highlight
      highlight={highlightPatterns}
      highlightStyles={theme => ({
        color: theme.colors.primary[7],
        backgroundColor: 'transparent',
        fontWeight: 700,
      })}
      {...textProps}
    >
      {children}
    </Highlight>
  );
}
